import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Aggregate session-cache state. Account data is intentionally kept only in
  // Redux memory; it is not included in redux-persist.
  accountLoadStatus: 'idle',      // 'idle' | 'loading' | 'ready'

  // Profile section
  profileStatus: 'idle',          // 'idle' | 'loading' | 'ready' | 'saving' | 'failed'
  profile: null,
  profileError: null,
  isEditing: false,
  profileDraft: {},

  // Security
  passwordResetStatus: 'idle',    // 'idle' | 'sending' | 'sent' | 'failed'
  passwordResetError: null,

  // Notification prefs (in-app, via checknotify)
  notifyPrefsStatus: 'idle',
  notifyPrefs: null,
  notifyPrefsError: null,

  // Speech prefs (Activate MIC, via checkspeech)
  speechPrefsStatus: 'idle',
  speechPrefs: null,
  speechPrefsError: null,

  // Billing
  ordersStatus: 'idle',
  orders: [],
  ordersError: null,
  certificatesStatus: 'idle',
  certificates: [],
  certificatesError: null,

  // Organization (B2B)
  licensesStatus: 'idle',
  licenses: null,
  licensesError: null,

  // Phone change OTP flow (Plan J)
  phoneChange: null,
};

const slice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    accountRequested(state) {
      state.accountLoadStatus = 'loading';
      state.profileStatus = 'loading';
      state.profileError = null;
      state.notifyPrefsStatus = 'loading';
      state.notifyPrefsError = null;
      state.speechPrefsStatus = 'loading';
      state.speechPrefsError = null;
      state.ordersStatus = 'loading';
      state.ordersError = null;
      state.certificatesStatus = 'loading';
      state.certificatesError = null;
      state.licensesStatus = 'loading';
      state.licensesError = null;
    },
    accountLoadFinished(state) {
      state.accountLoadStatus = 'ready';
    },
    // Opening the installed-app account sheet is an intent signal. Its saga
    // performs the session-cache check before starting any API requests.
    nativeAccountOpened() {},

    // Profile
    profileRequested(state) {
      state.profileStatus = 'loading';
      state.profileError = null;
    },
    profileLoaded(state, action) {
      state.profileStatus = 'ready';
      state.profile = action.payload;
    },
    profileLoadFailed(state, action) {
      state.profileStatus = 'failed';
      state.profileError = action.payload ?? 'Failed to load profile';
    },
    profileEditStarted(state) {
      if (!state.profile) return;
      state.isEditing = true;
      state.profileDraft = { ...state.profile };
    },
    profileDraftChanged(state, action) {
      const { field, value } = action.payload;
      state.profileDraft[field] = value;
    },
    profileEditCancelled(state) {
      state.isEditing = false;
      state.profileDraft = {};
      state.profileError = null;
    },
    profileSaveRequested(state) {
      state.profileStatus = 'saving';
      state.profileError = null;
      state.phoneChange = null;
    },
    // Single-field save (e.g., notification toggles) — does NOT enter edit mode.
    // Profile draft is left untouched so a concurrent ProfileSection edit is preserved.
    profileFieldSaveRequested(state) {
      state.profileStatus = 'saving';
      state.profileError = null;
      state.phoneChange = null;
    },
    profileSaved(state, action) {
      state.profileStatus = 'ready';
      state.profile = action.payload;
      // If this completion is from a full edit-mode save, exit edit mode.
      // Single-field saves (from NotificationPrefsSection toggles) leave the
      // edit state alone so an in-progress ProfileSection edit isn't disturbed.
      if (state.isEditing) {
        state.isEditing = false;
        state.profileDraft = {};
      }
    },
    profileSaveFailed(state, action) {
      state.profileStatus = 'failed';
      state.profileError = action.payload ?? 'Save failed';
      // Stay in edit mode; draft preserved.
    },

    // Security
    passwordResetRequested(state) {
      state.passwordResetStatus = 'sending';
      state.passwordResetError = null;
    },
    passwordResetSent(state) {
      state.passwordResetStatus = 'sent';
    },
    passwordResetFailed(state, action) {
      state.passwordResetStatus = 'failed';
      state.passwordResetError = action.payload ?? 'Could not send reset PIN';
    },
    passwordResetCooldownExpired(state) {
      if (state.passwordResetStatus === 'sent') {
        state.passwordResetStatus = 'idle';
      }
    },

    // Notify prefs
    notifyPrefsRequested(state) {
      state.notifyPrefsStatus = 'loading';
      state.notifyPrefsError = null;
    },
    notifyPrefsLoaded(state, action) {
      state.notifyPrefsStatus = 'ready';
      state.notifyPrefs = action.payload;
    },
    notifyPrefsLoadFailed(state, action) {
      state.notifyPrefsStatus = 'failed';
      state.notifyPrefsError = action.payload ?? 'Failed to load notification preferences';
    },
    notifyPrefsChanged(state, action) {
      // Optimistic update — store the new value AND mark saving.
      state.notifyPrefs = { ...(state.notifyPrefs ?? {}), ...action.payload };
      state.notifyPrefsStatus = 'saving';
    },
    notifyPrefsSaved(state) {
      state.notifyPrefsStatus = 'ready';
    },
    notifyPrefsSaveFailed(state, action) {
      // Revert to the previous server-known value, surface the error.
      const { previous, error } = action.payload ?? {};
      if (previous) state.notifyPrefs = previous;
      state.notifyPrefsStatus = 'failed';
      state.notifyPrefsError = error ?? 'Could not update preferences';
    },

    // Speech prefs
    speechPrefsRequested(state) {
      state.speechPrefsStatus = 'loading';
      state.speechPrefsError = null;
    },
    speechPrefsLoaded(state, action) {
      state.speechPrefsStatus = 'ready';
      state.speechPrefs = action.payload;
    },
    speechPrefsSynced(state, action) {
      state.speechPrefs = { ...(state.speechPrefs ?? {}), speech_is_on: !!action.payload };
      state.speechPrefsStatus = 'ready';
      state.speechPrefsError = null;
    },
    speechPrefsLoadFailed(state, action) {
      state.speechPrefsStatus = 'failed';
      state.speechPrefsError = action.payload ?? 'Failed to load microphone preference';
    },
    speechPrefsChanged(state, action) {
      state.speechPrefs = { ...(state.speechPrefs ?? {}), ...action.payload };
      state.speechPrefsStatus = 'saving';
      state.speechPrefsError = null;
    },
    speechPrefsSaved(state) {
      state.speechPrefsStatus = 'ready';
    },
    speechPrefsSaveFailed(state, action) {
      const { previous, error } = action.payload ?? {};
      if (previous) state.speechPrefs = previous;
      state.speechPrefsStatus = 'failed';
      state.speechPrefsError = error ?? 'Could not update microphone preference';
    },

    // Billing — orders
    ordersRequested(state) {
      state.ordersStatus = 'loading';
      state.ordersError = null;
    },
    ordersLoaded(state, action) {
      state.ordersStatus = 'ready';
      state.orders = action.payload ?? [];
    },
    ordersLoadFailed(state, action) {
      state.ordersStatus = 'failed';
      state.ordersError = action.payload ?? 'Failed to load orders';
    },

    // Billing — certificates
    certificatesRequested(state) {
      state.certificatesStatus = 'loading';
      state.certificatesError = null;
    },
    certificatesLoaded(state, action) {
      state.certificatesStatus = 'ready';
      state.certificates = action.payload ?? [];
    },
    certificatesLoadFailed(state, action) {
      state.certificatesStatus = 'failed';
      state.certificatesError = action.payload ?? 'Failed to load certificates';
    },

    // Organization
    licensesLoaded(state, action) {
      state.licensesStatus = 'ready';
      state.licenses = action.payload;
    },
    licensesLoadFailed(state, action) {
      state.licensesStatus = 'failed';
      state.licensesError = action.payload ?? 'Failed to load licenses';
      state.licenses = null;
    },

    // Phone change OTP flow (Plan J)
    phoneChangeStarted(state, action) {
      state.phoneChange = {
        draftPhone: action.payload.draftPhone,
        draft: action.payload.draft,
        status: 'requesting',
        error: null,
      };
    },
    phoneOtpSent(state) {
      if (state.phoneChange) state.phoneChange.status = 'sent';
    },
    phoneOtpRequestFailed(state, action) {
      if (state.phoneChange) {
        state.phoneChange.status = 'requestFailed';
        state.phoneChange.error = action.payload ?? 'Could not send code.';
      }
    },
    phoneOtpVerifyRequested(state) {
      if (state.phoneChange) state.phoneChange.status = 'verifying';
    },
    phoneOtpVerifyFailed(state, action) {
      if (state.phoneChange) {
        state.phoneChange.status = 'error';
        state.phoneChange.error = action.payload ?? "That code didn't match.";
      }
    },
    phoneOtpVerifySucceeded(state) {
      if (state.phoneChange) state.phoneChange.status = 'committing';
    },
    phoneChangeSucceeded(state, action) {
      if (action.payload) {
        state.profileStatus = 'ready';
        state.profile = action.payload;
        state.isEditing = false;
        state.profileDraft = {};
      }
      state.phoneChange = null;
    },
    phoneChangeFailed(state, action) {
      if (state.phoneChange) {
        state.phoneChange.status = 'error';
        state.phoneChange.error = action.payload ?? 'Could not update phone.';
      }
    },
    phoneChangeCancelled(state) {
      // Revert the draft phone field so the form re-shows the saved profile
      // phone, matching the spec's cancel-restore behavior. Other draft fields
      // (name, gender, etc.) stay intact — the user's non-phone edits aren't
      // collateral damage.
      if (state.profileDraft && state.profile) {
        state.profileDraft.phone = state.profile.phone;
      }
      state.phoneChange = null;
    },

    accountSliceReset() {
      return initialState;
    },
  },
});

export const {
  accountRequested,
  accountLoadFinished,
  nativeAccountOpened,
  profileRequested,
  profileLoaded,
  profileLoadFailed,
  profileEditStarted,
  profileDraftChanged,
  profileEditCancelled,
  profileSaveRequested,
  profileFieldSaveRequested,
  profileSaved,
  profileSaveFailed,
  passwordResetRequested,
  passwordResetSent,
  passwordResetFailed,
  passwordResetCooldownExpired,
  notifyPrefsRequested,
  notifyPrefsLoaded,
  notifyPrefsLoadFailed,
  notifyPrefsChanged,
  notifyPrefsSaved,
  notifyPrefsSaveFailed,
  speechPrefsRequested,
  speechPrefsLoaded,
  speechPrefsSynced,
  speechPrefsLoadFailed,
  speechPrefsChanged,
  speechPrefsSaved,
  speechPrefsSaveFailed,
  ordersRequested,
  ordersLoaded,
  ordersLoadFailed,
  certificatesRequested,
  certificatesLoaded,
  certificatesLoadFailed,
  licensesLoaded,
  licensesLoadFailed,
  phoneChangeStarted,
  phoneOtpSent,
  phoneOtpRequestFailed,
  phoneOtpVerifyRequested,
  phoneOtpVerifyFailed,
  phoneOtpVerifySucceeded,
  phoneChangeSucceeded,
  phoneChangeFailed,
  phoneChangeCancelled,
  accountSliceReset,
} = slice.actions;
export const accountReducer = slice.reducer;
